import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import Translate, { t } from '../common/Translate';
import ModalListSelect from './ModalListSelect';
import useValue from '../../services/Form/useValue';
import { useImage } from '../../services/Image/useImage';
import DisplayElement from './DisplayElement';
import { currencySign } from '../../Utils/currencySign';

const MultipleDamage = ({ elementIds, setDisableNav, setNavIsShown, goNextQuestion, question }) => {
  const { questionId, sectionId, formId } = elementIds;
  const [customCostModalShown, setCustomCostModalShown] = useState(false);
  const [children, , refreshChildren] = useValue(formId, sectionId, questionId, question.type, 'children');
  const [value, setValue] = useValue(formId, sectionId, questionId, question.type, 'value');
  const [selectedElement, setSelectedElement] = useState(null);
  const [label, setLabel] = useState('ok');

  useEffect(() => {
    if (setDisableNav !== undefined) {
      if (value !== null && value !== '' && value !== undefined) setDisableNav(false);
      else setDisableNav(true);
    }
    if (setNavIsShown !== undefined) {
      if (customCostModalShown) setNavIsShown(!customCostModalShown);
      else setNavIsShown(true);
    }
  });

  const hasDamage = () => {
    let res;
    if (children) {
      Object.values(children).forEach((v) => {
        if (v.value !== null && v.value !== undefined) res = true;
      });
    }
    return res;
  };

  const handleUpdateDamage = (v) => {
    setSelectedElement(v);
    setCustomCostModalShown(true);
  };

  // reset default value when delete damages
  useEffect(() => {
    if (!hasDamage() && ['add_damage', 'finish'].includes(value)) {
      setValue(null);
      setLabel('ok');
    }
    if (hasDamage()) setLabel('finish');
  }, [children]);

  const onClick = (option) => {
    if (option === 'add_damage') {
      setValue(option);
      setLabel('finish');
      setSelectedElement(null);
      return setCustomCostModalShown(true);
    }
    if (['ok', 'finish'].includes(option)) {
      if (value === option) return setValue(null);
      setValue(option);
    }
    if (goNextQuestion) goNextQuestion();
    return null;
  };

  const closeModal = async () => {
    setCustomCostModalShown(false);
    await refreshChildren();
  };

  return (
    <div className="before-sticky-bottom" data-cy={questionId}>
      <ModalListSelect
        formId={formId}
        sectionId={sectionId}
        listsOption={children}
        isOpen={customCostModalShown}
        closeModal={closeModal}
        questionId={questionId}
        selectedElement={selectedElement}
      />
      {children !== null &&
        children !== undefined &&
        Object.values(children).map((child) => (
          <AddedDamage
            key={child.id}
            id={child.id}
            element={child}
            questionId={questionId}
            sectionId={sectionId}
            formId={formId}
            refreshParent={refreshChildren}
            handleUpdateDamage={handleUpdateDamage}
          />
        ))}
      <Row>
        <Col xs="4" md="3" lg="3">
          <button
            type="button"
            color="primary"
            id="damage-cta"
            className={`btn btn-block btn-value h-100${['ok', 'finish'].includes(value) && ' selected'}`}
            data-cy={label}
            data-test={`${questionId}-${label}-button`}
            onClick={() => onClick(label)}
          >
            <Translate code={label} />
          </button>
        </Col>
        <Col xs="8" md="3" lg="3">
          <button
            type="button"
            className="btn btn-block btn-value h-100"
            data-cy="add_damage"
            data-test={`${questionId}-add_damage-button`}
            onClick={() => onClick('add_damage')}
          >
            <FontAwesomeIcon className="add-damage-button" size="2x" icon={faPlusCircle} />
            <Translate code="add_damage" />
          </button>
        </Col>
      </Row>
    </div>
  );
};

export default MultipleDamage;

MultipleDamage.propTypes = {
  elementIds: PropTypes.shape({
    questionId: PropTypes.string.isRequired,
    sectionId: PropTypes.string.isRequired,
    formId: PropTypes.string.isRequired,
  }).isRequired,
  setDisableNav: PropTypes.func,
  setNavIsShown: PropTypes.func,
  goNextQuestion: PropTypes.func,
  question: PropTypes.shape().isRequired,
};

MultipleDamage.defaultProps = {
  setDisableNav: undefined,
  setNavIsShown: undefined,
  goNextQuestion: undefined,
};

const AddedDamage = ({ id, element, formId, sectionId, questionId, refreshParent, handleUpdateDamage }) => {
  const pathImage0 = `inspection.children.${sectionId}.children.${questionId}.children.${element.id}.images.0.id`;
  const [image0, setImage0, deleteImage0] = useImage(formId, pathImage0, element.id, element.type);
  const [, setValue] = useValue(
    formId,
    sectionId,
    questionId,
    'multiple_damages',
    `children.${element.id}.value`,
    '',
    element.id,
    element.type,
  );
  const recordId = useSelector((state) => state.reduxRecords?.currentRecord);
  const country = useSelector((state) => state.reduxRecords.data[recordId]?.record?.country);
  if (element.value === null || element.value === undefined) {
    return null;
  }

  const handleUploadImage = (e) => {
    setImage0(e.target.files[0]);
  };

  const handleDeleteBlock = () => {
    setValue(null);
    deleteImage0();
    refreshParent();
  };

  const displayValue =
    typeof element.value === 'object' && element.value !== undefined
      ? `${Object.values(element.value)[0].label} ${currencySign(Object.values(element.value)[0].cost, country)} ${t(
          'TTC',
        )}`
      : t(element.value);
  return (
    <DisplayElement
      id={id}
      image={image0}
      element={element.id}
      displayValue={displayValue}
      handleDeleteBlock={handleDeleteBlock}
      handleUploadImage={handleUploadImage}
      handleUpdateDamage={handleUpdateDamage}
    />
  );
};

AddedDamage.propTypes = {
  id: PropTypes.string.isRequired,
  element: PropTypes.shape().isRequired,
  formId: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  refreshParent: PropTypes.func.isRequired,
  handleUpdateDamage: PropTypes.func.isRequired,
};
